import { Box, Divider } from '@mui/material';
import React, { ReactNode } from 'react';

import PageHeaderActionButtons from './PageHeaderActionButtons';
import PageHeaderActionsBar from './PageHeaderActionsBar';
import PageHeaderContainer from './PageHeaderContainer';
import PageHeaderFilters from './PageHeaderFilters';
import PageHeaderGlobalSearcher from './PageHeaderGlobalSearcher';
import type { PageHeaderIconType } from './pageHeaderIcons';
import PageHeaderMain from './PageHeaderMain';
import PageHeaderQuickActions from './PageHeaderQuickActions';
import PageHeaderTitleSection from './PageHeaderTitleSection';
import type { PageHeaderIconColorProps } from './PageHeaderTitleSection';
import PageHeaderToggleGroup from './PageHeaderToggleGroup';
import type { ActionButton, ActionChip, ToggleTab } from './types';
import type { PageHeaderVariant } from './types';

export type PageHeaderWithTitleAndActionsProps = {
  variant?: PageHeaderVariant;
  iconType?: PageHeaderIconType;
  icon?: ReactNode;
  title?: string;
  subtitle?: string;
  actionButtons?: ActionButton[];
  globalSearcherProps?: React.ComponentProps<typeof PageHeaderGlobalSearcher>;
  toggleTabs?: ToggleTab[];
  toggleValue?: string;
  onToggleChange?: (value: string) => void;
  quickActionChips?: ActionChip[];
  filters?: ReactNode;
} & PageHeaderIconColorProps;

export interface PageHeaderActionsOnlyProps {
  variant?: PageHeaderVariant;
  toggleTabs?: ToggleTab[];
  toggleValue?: string;
  onToggleChange?: (value: string) => void;
  quickActionChips?: ActionChip[];
  actionButtons?: ActionButton[];
  globalSearcherProps?: React.ComponentProps<typeof PageHeaderGlobalSearcher>;
}

export type PageHeaderTitleOnlyProps = {
  variant?: PageHeaderVariant;
  iconType?: PageHeaderIconType;
  icon?: ReactNode;
  title?: string;
  subtitle?: string;
} & PageHeaderIconColorProps;

export const PageHeaderWithTitleAndActions: React.FC<
  PageHeaderWithTitleAndActionsProps
> = ({
  variant = 'pageHeader',
  iconType,
  iconVariant,
  explicitColor,
  iconBackground,
  icon,
  title,
  subtitle,
  actionButtons = [],
  globalSearcherProps,
  toggleTabs,
  toggleValue,
  onToggleChange,
  quickActionChips = [],
  filters,
}) => {
  const hasActionsBar =
    toggleTabs != null || quickActionChips.length > 0 || filters != null;

  return (
    <PageHeaderContainer variant={variant}>
      <PageHeaderMain>
        <PageHeaderTitleSection
          iconType={iconType}
          icon={icon}
          title={title}
          subtitle={subtitle}
          {...(explicitColor != null && explicitColor !== ''
            ? { explicitColor, iconBackground }
            : { iconVariant })}
        />
        {actionButtons.length > 0 && (
          <PageHeaderActionButtons buttons={actionButtons} />
        )}
        {globalSearcherProps != null && (
          <PageHeaderGlobalSearcher {...globalSearcherProps} />
        )}
        {filters != null && <PageHeaderFilters>{filters}</PageHeaderFilters>}
      </PageHeaderMain>
      {hasActionsBar && (
        <>
          <Divider
            sx={(theme) => ({
              my: 1,
              borderColor: theme.palette.divider,
              opacity: 0.7,
            })}
          />
          <PageHeaderActionsBar>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              {toggleTabs != null &&
                toggleValue != null &&
                onToggleChange != null && (
                  <PageHeaderToggleGroup
                    tabs={toggleTabs}
                    value={toggleValue}
                    onChange={onToggleChange}
                  />
                )}
              {quickActionChips.length > 0 && (
                <PageHeaderQuickActions chips={quickActionChips} />
              )}
            </Box>
          </PageHeaderActionsBar>
        </>
      )}
    </PageHeaderContainer>
  );
};

export const PageHeaderActionsOnly: React.FC<PageHeaderActionsOnlyProps> = ({
  variant = 'pageHeader',
  toggleTabs,
  toggleValue,
  onToggleChange,
  quickActionChips = [],
  actionButtons = [],
  globalSearcherProps,
}) => {
  const hasLeftActions = toggleTabs != null || quickActionChips.length > 0;

  return (
    <PageHeaderContainer variant={variant}>
      <PageHeaderMain>
        {hasLeftActions && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flex: 1,
            }}
          >
            {toggleTabs != null &&
              toggleValue != null &&
              onToggleChange != null && (
                <PageHeaderToggleGroup
                  tabs={toggleTabs}
                  value={toggleValue}
                  onChange={onToggleChange}
                />
              )}
            {quickActionChips.length > 0 && (
              <PageHeaderQuickActions chips={quickActionChips} />
            )}
          </Box>
        )}
        {actionButtons.length > 0 && (
          <PageHeaderActionButtons buttons={actionButtons} />
        )}
        {globalSearcherProps != null && (
          <PageHeaderGlobalSearcher {...globalSearcherProps} />
        )}
      </PageHeaderMain>
    </PageHeaderContainer>
  );
};

export const PageHeaderTitleOnly: React.FC<PageHeaderTitleOnlyProps> = ({
  variant = 'pageHeader',
  iconType,
  iconVariant,
  explicitColor,
  iconBackground,
  icon,
  title,
  subtitle,
}) => {
  return (
    <PageHeaderContainer variant={variant}>
      <PageHeaderMain>
        <PageHeaderTitleSection
          iconType={iconType}
          icon={icon}
          title={title}
          subtitle={subtitle}
          {...(explicitColor != null && explicitColor !== ''
            ? { explicitColor, iconBackground }
            : { iconVariant })}
        />
      </PageHeaderMain>
    </PageHeaderContainer>
  );
};
